import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ProductMapping } from "../mappings/product/product-create/product-mappings";
import { IPaginationResponseModel } from "../models/generic/pagination/pagination-response.base.model";
import { IPaginationSearchRequestBaseModel } from "../models/generic/pagination/pagination-search-base.model";
import { IProductCoreGetResponseModel } from "../models/product/dto/product-core-get-response.model";
import { IProductCorePutRequestModel } from "../models/product/dto/product-core-put-request.model";
import { IProductCorePutResponseModel } from "../models/product/dto/product-core-put-response.model";
import { IProductGetDtoModel } from "../models/product/dto/product-create/product-get-dto.model";
import { IProducPosttDtoModel } from "../models/product/dto/product-create/product-post-dto.model";
import { IProductModel } from "../models/product/product.model";
import { ProductRequestPostSupersedeModel } from "../models/product/request/product-supersede.model";
import { IProductResultResponseModel } from "../models/product/response/products-result-response.model";
import { ISearchProducts } from "../models/product/search/search.model";
import { EnvironmentService } from "./environment.service";
import { ApplicationContextService } from "./application-context.service";
import { ProductVariations } from "../models/product/product-variation.model";

@Injectable({
    providedIn: 'root'
  })
export class ProductService {
   fullPath: string;
   private delay = 3000;
   mapping: ProductMapping;
   branchCode: string;
   
   constructor(private readonly httpClient: HttpClient, public envService: EnvironmentService,
      private _applicationContextService: ApplicationContextService) {
      this.fullPath =  `${envService.settings.apiUrl}/products`;
      this.branchCode = 'SNG'; //  envService.settings.branch.branchCode;
      this.mapping = new ProductMapping();
   }
   delete(model: IProductModel): Observable<boolean> {
      throw new Error("Method not implemented.");
   }

   public update(model: IProductCorePutRequestModel): Observable<IProductCorePutResponseModel> {
     return this.httpClient.put<IProductCorePutResponseModel>(`${this.fullPath}/${model.productId}/core`, model);
   }

   public save(model: IProducPosttDtoModel): Observable<IProducPosttDtoModel> {
      return this.httpClient.post<IProducPosttDtoModel>(this.fullPath, model);
   }

   public get(id: string): Observable<IProductCoreGetResponseModel|undefined> {
      return this.httpClient.get<IProductCoreGetResponseModel>(`${this.fullPath}\\${id}\\core`);
   }

   public summary(id: string): Observable<IProductCoreGetResponseModel|undefined> {
      return this.httpClient.get<IProductCoreGetResponseModel>(`${this.fullPath}\\${id}\\summary?siteCode=${this._applicationContextService.getBranchCodeFromUrl()}`);
   }

   public search(paginationSearchModel: IPaginationSearchRequestBaseModel<ISearchProducts>): Observable<IPaginationResponseModel<IProductResultResponseModel>> {
      const mapping = new ProductMapping();
      let params = new HttpParams();

      localStorage.setItem('previousSearch', JSON.stringify({
         'mode': paginationSearchModel?.searchModel?.mode,
         'partNumber': paginationSearchModel?.searchModel?.partNumber,
         'description': paginationSearchModel?.searchModel?.description,
         'supplierPartNumber': paginationSearchModel?.searchModel?.supplierPartNumber,
         'pageNo': paginationSearchModel.currentpage,
         'includeComponentParts': paginationSearchModel?.searchModel?.includeComponentParts,
         'includeTurnedDown': paginationSearchModel?.searchModel?.includeTurnedDown,
         'searchDate' : new Date()
      }))
            
      if (paginationSearchModel != null) {
         if ( paginationSearchModel?.searchModel?.description) {
            params = params.append("description", paginationSearchModel?.searchModel?.description);
         }

         if ( paginationSearchModel?.searchModel?.partNumber) {
            params = params.append("partNumber", paginationSearchModel?.searchModel?.partNumber);
         }

         if ( paginationSearchModel?.searchModel?.supplierPartNumber) {
            params = params.append("supplierPartNumber", paginationSearchModel?.searchModel?.supplierPartNumber);
         }

         if ( paginationSearchModel?.searchModel?.includeComponentParts) {
            params = params.append("includeComponentParts", paginationSearchModel?.searchModel?.includeComponentParts);
         }

         if ( paginationSearchModel?.searchModel?.includeTurnedDown) {
            params = params.append("includeTurnedDown", paginationSearchModel?.searchModel?.includeTurnedDown);
         }

         params = params.append("siteCode", this._applicationContextService.getBranchCodeFromUrl());
         params = params.append("currentPage", `${paginationSearchModel.currentpage}`);
         params = params.append("rowsPerPage", `${paginationSearchModel.rowsperpage}`);
      }

      return this.httpClient.get<any>(this.fullPath,{ params }).pipe(
          map(dtos => {
                return {
                    total: dtos.total,
                    data: dtos.products ? (dtos.products as IProductResultResponseModel []) : []
                } as IPaginationResponseModel<IProductResultResponseModel>
            })
      );
   }

   public map(model: any): IProductGetDtoModel {
      return {
         partNumber: 'string',
         description: 'string',
         quantity: 1,
         unitPride: 'string',
         includeTurnedDownParts: true,
         includeComponentParts: true,
         shortTitle: 'string',
         countryOfOrigin: 'string',
         ukCommodityCode: 'string',
         usCommodityCode: 'string',
         taxRateCategory: 0
     } as IProductGetDtoModel
   }

   public getCurrencyCode(branchCode?: string): string {

      if(!branchCode){
         branchCode = this._applicationContextService.getBranchCodeFromUrl();
      }

      for (let i = 0; i < this.envService.settings.branches.length; i++) {
         if(branchCode == this.envService.settings.branches[i].code){
            return this.envService.settings.branches[i].currencyCode;
         }
      }

      return 'GBP'

   }

   public getNextSortOrderValue(array: any[]) {
      if (array && array.length) {
         const lastElementOfArray = array[array.length - 1];
         let sortOrderValue = lastElementOfArray.sortOrder;
   
         return sortOrderValue + 1;
      }
   }

   public freeze(model: string): Observable<any> {
      return this.httpClient.post<any>(`${this.fullPath}/${model}/freeze`, {});
   }

   public supersede(model: ProductRequestPostSupersedeModel): Observable<any> {
      return this.httpClient.post<any>(`${this.fullPath}/${model.id}/supersede`, model);
   }

   public turnDown(model: string): Observable<any> {
      return this.httpClient.post<any>(`${this.fullPath}/${model}/turndown`, {});
   }

   public makeAvailable(model: string): Observable<any> {
      return this.httpClient.post<any>(`${this.fullPath}/${model}/reinstate`, {});
   }

   public getVariations(productId: number): Observable<ProductVariations>{
      return this.httpClient.get<ProductVariations>(`${this.fullPath}/${productId}/variations?branchCode=${this._applicationContextService.getBranchCodeFromUrl()}`);
   }

   public updateBranchProductStatus(model: any): Observable<any> {
      return this.httpClient.put<any>(`${this.fullPath}/applyNewBranchProductStatus?branchCode=${model.branchCode}`, model);
   }

   public updateDiscountCode(model: any): Observable<any> {
      return this.httpClient.put<any>(`${this.fullPath}/${model.productId}/discountCategoryCode/${model.discountCategoryCode}`, {});
   }

   public getIntraGroupPrice(averageCostPrice: number): number {

      let intraGroupPriceMarkUp = this.envService.settings.intraGroupPriceMarkUp;

      if(intraGroupPriceMarkUp){
         return averageCostPrice + ((intraGroupPriceMarkUp * averageCostPrice) / 100);
       }else{
         return 0;
       }
   }
}

